// 1080x1080 shareable Milestone Certificate — 3D-styled Robot Pip with his
// Golden Crown (gradient shading; the interactive app stays 2D by design).
// Square format optimized for WhatsApp & Instagram.
// NOTE: gradient ids are unique per instance (useId) — multiple cards on one
// page (Achievements grid + hidden share renders) would otherwise collide and
// resolve url(#...) refs to a hidden SVG, leaving white-on-white text.
export const CertificateSVG = ({
  svgRef,
  name,
  streak,
  badges,
  link,
  headline: headlineProp,
  milestone: milestoneProp,
}) => {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const g = (n) => `${n}${uid}`;
  const gu = (n) => `url(#${n}${uid})`;
  const headline = headlineProp || (name ? `${name} did it!` : "We did it!");
  const milestone =
    milestoneProp ||
    (streak > 0 || badges > 0
      ? [
          streak > 0 && `${streak}-day practice streak`,
          badges > 0 && `${badges} badge${badges === 1 ? "" : "s"} earned`,
        ]
          .filter(Boolean)
          .join("  ·  ")
      : "Growing stronger every day");
  return (
    <svg
      ref={svgRef}
      viewBox="0 0 1080 1080"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full rounded-xl border border-border"
      role="img"
      aria-label="Milestone certificate"
      data-testid="certificate-preview"
    >
      <defs>
        <radialGradient id={g("cbg")} cx="50%" cy="38%" r="75%">
          <stop offset="0%" stopColor="#16233B" />
          <stop offset="100%" stopColor="#070B14" />
        </radialGradient>
        <linearGradient id={g("cbody")} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#6EE7B7" />
          <stop offset="45%" stopColor="#34D399" />
          <stop offset="100%" stopColor="#0E9F6E" />
        </linearGradient>
        <linearGradient id={g("cplate")} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F8FAFC" />
          <stop offset="100%" stopColor="#B6C2D2" />
        </linearGradient>
        <linearGradient id={g("cgold")} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FDE68A" />
          <stop offset="55%" stopColor="#F59E0B" />
          <stop offset="100%" stopColor="#B45309" />
        </linearGradient>
        <radialGradient id={g("ceye")} cx="35%" cy="30%" r="80%">
          <stop offset="0%" stopColor="#A5F3FC" />
          <stop offset="60%" stopColor="#22D3EE" />
          <stop offset="100%" stopColor="#0E7490" />
        </radialGradient>
        <radialGradient id={g("chalo")} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#34D399" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#34D399" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="1080" height="1080" fill="#0B1226" />
      <rect width="1080" height="1080" fill={gu("cbg")} />
      {[
        [140, 130],
        [930, 170],
        [850, 930],
        [190, 880],
        [540, 80],
        [990, 520],
        [90, 480],
      ].map(([cx, cy], i) => (
        <circle
          key={`${cx}-${cy}`}
          cx={cx}
          cy={cy}
          r={i % 2 ? 3 : 4.5}
          fill="#94a3b8"
          opacity="0.5"
        />
      ))}
      <rect
        x="46"
        y="46"
        width="988"
        height="988"
        rx="40"
        fill="none"
        stroke={gu("cgold")}
        strokeWidth="6"
        opacity="0.85"
      />
      <text
        x="540"
        y="150"
        textAnchor="middle"
        fill="#FBBF24"
        fontFamily="Arial, sans-serif"
        fontSize="34"
        fontWeight="bold"
        letterSpacing="10"
      >
        MILESTONE CERTIFICATE
      </text>
      <circle cx="540" cy="420" r="230" fill={gu("chalo")} />
      <g transform="translate(420 260) scale(2.1)">
        <ellipse cx="57" cy="122" rx="46" ry="9" fill="#000" opacity="0.35" />
        <polygon
          points="30,16 42,-9 58,10 74,-11 88,14 84,27 34,27"
          fill={gu("cgold")}
          stroke="#92400E"
          strokeWidth="2"
        />
        <circle cx="42" cy="-6" r="4" fill="#FDE68A" />
        <circle cx="74" cy="-8" r="4" fill="#FDE68A" />
        <rect
          x="12"
          y="24"
          width="96"
          height="88"
          rx="26"
          fill={gu("cbody")}
          stroke="#065F46"
          strokeWidth="2.5"
        />
        <ellipse cx="40" cy="38" rx="22" ry="9" fill="#D1FAE5" opacity="0.55" />
        <rect
          x="26"
          y="42"
          width="68"
          height="44"
          rx="14"
          fill={gu("cplate")}
        />
        <circle cx="46" cy="62" r="9" fill={gu("ceye")} />
        <circle cx="74" cy="62" r="9" fill={gu("ceye")} />
        <circle cx="49" cy="58.5" r="3" fill="#F0FDFF" />
        <circle cx="77" cy="58.5" r="3" fill="#F0FDFF" />
        <path
          d="M46 96 Q60 108 74 96"
          stroke="#065F46"
          strokeWidth="4.5"
          fill="none"
          strokeLinecap="round"
        />
        <rect x="1" y="52" width="11" height="30" rx="5.5" fill="#0E9F6E" />
        <rect x="108" y="52" width="11" height="30" rx="5.5" fill="#0E9F6E" />
      </g>
      <text
        x="540"
        y="620"
        textAnchor="middle"
        fill="#FFFFFF"
        fontFamily="Georgia, serif"
        fontSize={headline.length > 26 ? 52 : 72}
        fontWeight="bold"
      >
        {headline}
      </text>
      <text
        x="540"
        y="685"
        textAnchor="middle"
        fill="#CBD5E1"
        fontFamily="Arial, sans-serif"
        fontSize="34"
      >
        {milestone}
      </text>
      <rect
        x="240"
        y="740"
        width="600"
        height="80"
        rx="40"
        fill="#FBBF24"
        opacity="0.14"
        stroke="#FBBF24"
        strokeWidth="2"
      />
      <text
        x="540"
        y="792"
        textAnchor="middle"
        fill="#FBBF24"
        fontFamily="Arial, sans-serif"
        fontSize="34"
        fontWeight="bold"
      >
        Practicing with Pip
      </text>
      <text
        x="540"
        y="925"
        textAnchor="middle"
        fill="#CBD5E1"
        fontFamily="Arial, sans-serif"
        fontSize="26"
      >
        {link}
      </text>
      <text
        x="540"
        y="985"
        textAnchor="middle"
        fill="#64748B"
        fontFamily="Arial, sans-serif"
        fontSize="18"
      >
        M Speech, Language &amp; Math Helper
      </text>
    </svg>
  );
};

// Rasterize the certificate SVG to a 1080x1080 PNG blob.
export const certificatePng = (svgNode) =>
  new Promise((resolve, reject) => {
    const svg = new XMLSerializer().serializeToString(svgNode);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 1080;
      canvas.height = 1080;
      canvas.getContext("2d").drawImage(img, 0, 0, 1080, 1080);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("render failed"))),
        "image/png",
      );
    };
    img.onerror = reject;
    img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
  });

// Drop-in "Share Success" button for any milestone moment (boss wins, streaks,
// level-ups). Renders a hidden certificate, then native-shares (or downloads)
// the 1080x1080 PNG.
import { useId, useRef, useState } from "react";
import { Share2 } from "lucide-react";
import { toast } from "sonner";

export const ShareSuccessButton = ({
  firstName,
  headline,
  milestone,
  testId = "share-success-button",
}) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const link = window.location.origin;

  const share = async () => {
    setBusy(true);
    try {
      const blob = await certificatePng(ref.current);
      const file = new File([blob], "pip-milestone-certificate.png", {
        type: "image/png",
      });
      const text = `${headline} ${milestone} on M Speech, Language & Math Helper — ${link}`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: headline,
          text,
          url: link,
        });
        toast.success("Shared!");
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "pip-milestone-certificate.png";
        a.click();
        URL.revokeObjectURL(url);
        toast.success(
          "Certificate downloaded — perfect for WhatsApp or Instagram",
        );
      }
    } catch (e) {
      if (e?.name !== "AbortError")
        toast.error("Couldn't create the certificate");
    }
    setBusy(false);
  };

  return (
    <>
      <button
        type="button"
        disabled={busy}
        onClick={share}
        className="inline-flex items-center gap-1.5 rounded-full bg-amber-400 text-slate-900 font-extrabold text-sm px-5 h-10 hover:bg-amber-300 transition-colors disabled:opacity-60"
        data-testid={testId}
      >
        <Share2 className="h-4 w-4" aria-hidden="true" />{" "}
        {busy ? "Creating…" : "Share Success"}
      </button>
      <div className="hidden" aria-hidden="true">
        <CertificateSVG
          svgRef={ref}
          name={firstName}
          headline={headline}
          milestone={milestone}
          link={link}
        />
      </div>
    </>
  );
};
